import { Box, Chip, Typography } from '@mui/material';
import DocumentScannerOutlinedIcon from '@mui/icons-material/DocumentScannerOutlined';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Billing_Type_Enum, Order_Status_Enum } from '@kheops/graphql';
import { FormatBaseUnitQuantity, FormatLogisticalPackaging, round } from '@kheops/utils';
import useCurrencyFormat from '../../hooks/useCurrencyFormat';
import i18n from '../../i18n';
import AddedPackagingBadge from '../../orders/tracking-page/AddedPackagingBadge';
import { PackagingDisplay } from '../../orders/state/state';
import BillingTypeCard from '../../products/BillingTypeCard';
import FileUtils from '../utils/file.utils';
import useOrderItem from '../../hooks/useOrderItem';
import { ellipsisStyle } from '../utils/style.utils';
import PackagingWeightEditBox from './PackagingWeightEditBox';
import SizedImage from './SizedImage';
import VersionedValue from './VersionedValue';
import OrderItemEditBox from './OrderItemEditBox';

interface PackagingDisplayBox {
  orderStatus: Order_Status_Enum;
  packagingDisplay: PackagingDisplay;
  index: number;
  orderId?: string;
  canEditQuantity?: boolean;
  canEditWeight?: boolean;
  shouldEditWeightBeEnabledByDefault?: boolean;
  shouldEditQuantityBeEnabledByDefault?: boolean;
  shouldDisplayAddedPackagingBadge?: boolean;
}
export default function PackagingDisplayBox({
  orderStatus,
  packagingDisplay,
  index,
  orderId,
  canEditQuantity,
  canEditWeight,
  shouldEditWeightBeEnabledByDefault,
  shouldEditQuantityBeEnabledByDefault,
  shouldDisplayAddedPackagingBadge,
}: PackagingDisplayBox): React.JSX.Element {
  const { t } = useTranslation(['common', 'price']);
  const currencyFormat = useCurrencyFormat();
  const numberFormat = new Intl.NumberFormat(i18n.resolvedLanguage, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0,
  });
  const { price, previousPrice, defaultPrice } = useOrderItem(packagingDisplay.id) || {};
  const isWeightDependent = useMemo(() => (
    packagingDisplay.base_unit?.billing_type === Billing_Type_Enum.WeightDependent
  ), [packagingDisplay]);

  const roundedDefaultPrice = defaultPrice ? round(defaultPrice) : undefined;
  const roundedPreviousPrice = previousPrice ? round(previousPrice) : undefined;
  const roundedPrice = price ? round(price) : undefined;

  return (
    <Box
      sx={{
        py: 1.5,
        display: 'flex',
        width: '100%',
        gap: 2,
        flexDirection: { xs: 'column', lg: 'row' },
        justifyContent: 'space-between',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          minWidth: 0,
        }}
      >
        <SizedImage
          src={FileUtils.BuildPackagingImageUrl({
            photo: packagingDisplay.base_unit?.main_photo,
            type: packagingDisplay.product!.sub_family!,
            size: { height: 160, fit: 'contain' },
          })}
          borderRadius={3}
          alt={t('products:csu')}
          width={72}
          height={72}
        />
        <Box sx={{ mt: 1, ml: 1.5, overflow: 'hidden' }}>
          <Typography
            variant="bodyMedium"
            sx={{
              ...ellipsisStyle,
              overflowWrap: 'anywhere',
            }}
          >
            {packagingDisplay.product?.name}
          </Typography>
          <Typography variant="bodySmall" color="onSurfaceVariant" sx={{ mb: 1 }}>
            {FormatBaseUnitQuantity(packagingDisplay.base_unit!, 'fr', numberFormat) || ''} - {FormatLogisticalPackaging(packagingDisplay, 'fr', packagingDisplay.base_unit!) || ''}
          </Typography>
          {packagingDisplay.base_unit?.gtin && (
            <Chip
              variant="tag"
              icon={<DocumentScannerOutlinedIcon sx={{ transform: 'rotate(90deg)' }} />}
              label={packagingDisplay.base_unit.gtin}
            />
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              flexDirection: {
                sm: 'row',
              },
              mt: 0.5,
              gap: 0.5,
            }}
          >
            {isWeightDependent && (
              <BillingTypeCard
                billingType={Billing_Type_Enum.WeightDependent}
                inlineVersion
              />
            )}
            {(shouldDisplayAddedPackagingBadge) && (
              <AddedPackagingBadge />
            )}
          </Box>

        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, pt: 1 }}>
        <Box sx={{ width: { xs: '100%', lg: 116 } }}>
          <Typography variant="bodySmall" sx={{ mb: 0.5 }}>
            {`${t('common:quantity')}`}
          </Typography>
          <OrderItemEditBox
            packagingId={packagingDisplay.id}
            orderStatus={orderStatus}
            canEditQuantity={canEditQuantity}
            shouldEditBeEnabledByDefault={shouldEditQuantityBeEnabledByDefault}
            index={index}
          />
        </Box>
        <Box sx={{ width: { xs: '100%', lg: 116 } }}>
          {isWeightDependent
            ? (
              <>
                <Typography variant="bodySmall" sx={{ mb: 0.5 }}>
                  {t('common:total_weight')}
                </Typography>
                <PackagingWeightEditBox
                  packagingId={packagingDisplay.id}
                  canEditWeight={canEditWeight}
                  shouldEditBeEnabledByDefault={shouldEditWeightBeEnabledByDefault}
                  orderStatus={orderStatus}
                  orderId={orderId}
                />
              </>
            )
            : (
              <>
                <Typography variant="bodySmall" sx={{ mb: 0.5 }}>
                  {t('price:price_without_tax_per_packaging')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    height: 40,
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="bodyMedium">{currencyFormat.format(round(packagingDisplay.price))}</Typography>
                </Box>
              </>
            )}
        </Box>
        <Box sx={{ width: { xs: '100%', lg: 116 } }}>
          {roundedPrice !== undefined && (
            <>
              <Typography variant="bodySmall" sx={{ mb: 0.5 }}>
                {t('price:total_price_without_tax')}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  height: 40,
                  alignItems: 'center',
                }}
              >
                <VersionedValue
                  currentValue={roundedPrice}
                  previousValue={roundedDefaultPrice !== roundedPrice ? roundedDefaultPrice : roundedPreviousPrice}
                  color="onSurface.main"
                  valueFormatter={currencyFormat.format}
                  shouldHighlightDifference
                  variant="titleSmall"
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
